/**
 * Centralized UI feature gates for the extension.
 *
 * Keep these as simple booleans so we can quickly toggle
 * surfaces on/off without touching underlying business logic.
 */
// Build-time gate: the standalone mobile-web SPA build sets `MOBILE_WEBAPP=true`
// (see the `web:mobile` / `build:web:mobile` scripts). Gate on this POSITIVE
// signal, not on the absence of `WEB_ENGINE`. A negative gate (`!WEB_ENGINE`)
// silently inverts if `WEB_ENGINE` leaks into the mobile-web build env (e.g. a
// CI shell that exported `WEB_ENGINE=webkit` for a prior extension build): it
// flips `railgunEngineOffscreenEnabled` ON and `railgunWorkerIsolationEnabled`
// OFF, so every private spend routes to an offscreen host that does not exist in
// a web app and throws "Railgun offscreen host not available on this target".
// Compiled to a literal by babel `transform-inline-environment-variables`.
const isMobileWebBuild = process.env.MOBILE_WEBAPP === 'true'

// Build-time gate: the DEV variant of the mobile-web build. `MOBILE_WEBAPP_DEV`
// is read from `.env` (loaded into process.env by Expo, babel-inlined) — flip it
// to "true" there for a dev build. It points the app at the
// `apis.dev.zkxwallet.com` backend and surfaces dev-only surfaces (Swap &
// Bridge, dApps/WalletConnect) that the production mobile-web build hides per
// ZKX product decision. ANDed with `isMobileWebBuild` so a leftover "true" in
// `.env` has NO effect on the extension / native builds (which never set
// MOBILE_WEBAPP); the gated UI tree-shakes out of those builds.
const isMobileWebDevBuild = isMobileWebBuild && process.env.MOBILE_WEBAPP_DEV === 'true'

// Build-time gate: the offscreen document API is Chromium-only. webkit-safari
// does not implement it, and gecko's surface is divergent. The mobile-web build
// has no offscreen host at all, so force this OFF there even if `WEB_ENGINE`
// leaks in — `isMobileWebBuild` wins.
const isChromiumWebkit = process.env.WEB_ENGINE === 'webkit' && !isMobileWebBuild

export const FEATURES = {
  gasTankUiEnabled: false,
  smartAccountEnabled: false,
  smartAccountImportEnabled: false,
  // Phase 1 + Phase 2 of RAILGUN_HYBRID_REWRITE_PLAN.md.
  // When true on a Chromium build, the RAILGUN engine boots inside an offscreen
  // document instead of the service worker. Default OFF — flip to true to dogfood.
  // Has no effect on gecko/safari builds (the offscreen entry isn't bundled there).
  railgunEngineOffscreenEnabled: isChromiumWebkit && true,
  // A1 keystore-isolation (mobile-web only), §13 of
  // docs/phase4-railgun-isolation-vi.md. When true the RAILGUN engine runs in a
  // dedicated Web Worker spawned *nested* under the keystore worker, not on the
  // page, so a page-context XSS cannot read the seed/encryptionKey or drive a
  // spend directly. Default OFF — only honored on the mobile-web build, where
  // the keystore worker exists (`WebViewWorker.web.tsx`). On the offscreen
  // (webkit) build the keystore worker does not exist, so routing wallet load /
  // spend through it would hang forever (no worker answers the RPC) — which is why
  // this MUST stay build-gated to mobile-web and not be a bare `true`. The
  // `handleLoadRailgunWallets` / spend paths read this flag unconditionally, so a
  // bare `true` is NOT inert on the extension: it strands `walletInstances` empty
  // and the private Activity tab shows no history. Flip the gate, not the value,
  // to dogfood on `yarn web:mobile`.
  railgunWorkerIsolationEnabled: isMobileWebBuild && true,
  // Phase 5 of docs/bao-mat-ban-web-va-passkey-vi.md (mobile-web only). When true,
  // surfaces WebAuthn-PRF passkey registration (Settings) + unlock
  // (KeyStoreUnlockScreen) as an additional keystore secret alongside the password.
  // Password stays as the recovery path. Default OFF — flip to dogfood on a secure
  // context (https / localhost). Inert if the browser lacks PRF support.
  passkeyUnlockEnabled: true,
  // Per-chain balance sync state machine (RAILGUN_BALANCE_SYNC_REFACTOR_PLAN_VI.md).
  // When true, UI adopts the atomic `accountSnapshots` field published by the SW
  // instead of merging from `balanceSummaries` + `balanceMapsByAccount`. Default OFF
  // — rollback path: flip false and UI falls back to legacy mirror effects.
  railgunPerChainSync: true,
  // "Forgot password" full-reset recovery (mobile-web only). Surfaces a
  // "Forgot password?" action on the unlock screen that wipes the keystore +
  // accounts + seeds (all app IndexedDB) and reloads into fresh onboarding, where
  // the user re-imports via Secret Recovery Phrase and sets a NEW password. The
  // keystore main key encrypts all keys with the password, so a forgotten password
  // can only be recovered by re-deriving keys from the seed — there is no in-place
  // password reset. Gated to mobile-web because the reset helper uses
  // `indexedDB.deleteDatabase` / `window.location`, which are meaningless on native
  // RN. `isMobileWebBuild` is babel-inlined, so this tree-shakes out of native /
  // extension builds.
  forgotPasswordResetEnabled: isMobileWebBuild && true,
  // Debug-only: forward the NESTED railgun worker's console (log/warn/error) up
  // the worker chain (railgun.worker → keystore worker → page) so it prints in the
  // page DevTools console, prefixed `[railgun.worker]`. The railgun engine runs two
  // worker levels deep, so its console never surfaces in the page console without
  // recursive CDP auto-attach — this flag makes it visible in plain DevTools. Set
  // `RAILGUN_WORKER_LOG=true` in the build env to enable (e.g. on `yarn web:mobile`).
  // `process.env.RAILGUN_WORKER_LOG` is babel-inlined (transform-inline-environment-
  // variables runs over src/), so when unset the forwarder tree-shakes out. Gated to
  // mobile-web because that is the only build with the nested-worker chain. Keep OFF
  // for releases — engine logs are very verbose.
  railgunWorkerLogForwarding: isMobileWebBuild && process.env.RAILGUN_WORKER_LOG === 'true',
  // Mobile-web build: surface the Swap & Bridge nav entry (sidebar + FAB).
  // Re-enabled for production (ZKX product decision, 2026-07-07) — public
  // swap ships in prod again; only the PRIVATE (RAILGUN) swap form stays
  // dev-gated, see `mobileWebPrivateSwapEnabled` below. The swap-and-bridge
  // route itself is always mounted; this only controls nav visibility.
  mobileWebSwapBridgeEnabled: isMobileWebBuild,
  // Mobile-web build: surface the dApps catalog + WalletConnect nav entries.
  // Re-enabled for production (ZKX product decision, 2026-07-07).
  mobileWebDappsEnabled: isMobileWebBuild,
  // Mobile-web DEV build only: allow swapping FROM the private (RAILGUN)
  // balance on the Swap & Bridge screen. Hidden in production — the swap nav
  // entry is public-only there; entering PRIVATE app mode on the swap screen
  // bounces back to public (see SwapAndBridgeScreen.web.tsx). Public/DEX swap
  // stays enabled in prod via `mobileWebSwapBridgeEnabled`.
  mobileWebPrivateSwapEnabled: isMobileWebDevBuild
} as const

export const isRailgunOffscreenSupported = isChromiumWebkit

